import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { PieLabelRenderProps } from 'recharts';
import type { Call, CallImpactResponse } from '../types';
import './Charts.css';

function renderPieLabel({ name, value }: PieLabelRenderProps): string {
  return `${name ?? ''}: ${value ?? 0}`;
}

const BLUE_PALETTE = ['#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#1d4ed8', '#1e40af'];

const STATUS_COLORS: Record<string, string> = {
  completed: '#2563eb',
  missed: '#ef4444',
  other: '#94a3b8',
};

interface CallsProps {
  calls: Call[];
}

interface ChartsProps {
  data: CallImpactResponse;
}

function CallStatusChart({ calls }: CallsProps) {
  const statusCounts: Record<string, number> = {};
  calls.forEach((c) => {
    const s = c.call_status || 'unknown';
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  });
  const pieData = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));

  return (
    <div className="chart-card">
      <h3 className="chart-title">Call Status</h3>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={90}
            dataKey="value"
            label={renderPieLabel}
          >
            {pieData.map((entry, i) => (
              <Cell
                key={entry.name}
                fill={STATUS_COLORS[entry.name] || BLUE_PALETTE[i % BLUE_PALETTE.length]}
              />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function InterestChart({ calls }: CallsProps) {
  const interested = calls.filter((c) => c.is_interested === true).length;
  const notInterested = calls.filter((c) => c.is_interested === false).length;
  const unknown = calls.filter((c) => c.is_interested === null).length;

  const pieData = [
    { name: 'Interested', value: interested },
    { name: 'Not Interested', value: notInterested },
    { name: 'Unknown', value: unknown },
  ].filter((d) => d.value > 0);

  const colors = ['#2563eb', '#f87171', '#94a3b8'];

  return (
    <div className="chart-card">
      <h3 className="chart-title">Interest Breakdown</h3>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={90}
            dataKey="value"
            label={renderPieLabel}
          >
            {pieData.map((entry, i) => (
              <Cell key={entry.name} fill={colors[i]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function QualificationFunnel({ calls }: CallsProps) {
  const completed = calls.filter((c) => c.call_status === 'completed').length;
  const interested = calls.filter((c) => c.is_interested === true).length;
  const qualified = calls.filter((c) => c.call_summary?.qualified === true).length;
  const fullyCompleted = calls.filter(
    (c) => c.call_summary?.qualification_stage_reached === 'completed'
  ).length;

  const funnelData = [
    { name: 'Completed Calls', value: completed, fill: '#bfdbfe' },
    { name: 'Interested', value: interested, fill: '#93c5fd' },
    { name: 'Qualified', value: qualified, fill: '#3b82f6' },
    { name: 'Fully Completed', value: fullyCompleted, fill: '#1d4ed8' },
  ];

  return (
    <div className="chart-card">
      <h3 className="chart-title">Qualification Funnel</h3>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={funnelData} layout="vertical" margin={{ left: 20, right: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis type="number" />
          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
          <Tooltip />
          <Bar dataKey="value" radius={[0, 6, 6, 0]}>
            {funnelData.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function DurationDistribution({ calls }: CallsProps) {
  const buckets = [
    { name: '0-10s', min: 0, max: 10 },
    { name: '11-30s', min: 11, max: 30 },
    { name: '31-60s', min: 31, max: 60 },
    { name: '61-120s', min: 61, max: 120 },
    { name: '120s+', min: 121, max: Infinity },
  ];

  const data = buckets.map((b) => ({
    name: b.name,
    count: calls.filter(
      (c) =>
        c.call_status === 'completed' &&
        c.call_duration != null &&
        c.call_duration >= b.min &&
        c.call_duration <= b.max
    ).length,
  }));

  return (
    <div className="chart-card">
      <h3 className="chart-title">Call Duration Distribution</h3>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ left: 0, right: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Bar dataKey="count" fill="#3b82f6" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function LoanStatusChart({ calls }: CallsProps) {
  const statusCounts: Record<string, number> = {};
  calls.forEach((c) => {
    const s = c.loan_status || 'Unknown';
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  });
  const data = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));

  return (
    <div className="chart-card">
      <h3 className="chart-title">Loan Status Breakdown</h3>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={90}
            dataKey="value"
            label={renderPieLabel}
          >
            {data.map((entry, i) => (
              <Cell key={entry.name} fill={BLUE_PALETTE[i % BLUE_PALETTE.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function InterestPercentageChart({ calls }: CallsProps) {
  const callsWithPct = calls
    .filter((c) => c.call_summary?.interest_percentage != null && c.call_status === 'completed')
    .map((c, i) => ({
      name: c.phone_10 || `Call ${i + 1}`,
      interest: c.call_summary!.interest_percentage,
    }));

  if (callsWithPct.length === 0) return null;

  return (
    <div className="chart-card chart-card-wide">
      <h3 className="chart-title">Interest Percentage per Call</h3>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={callsWithPct} margin={{ left: 0, right: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" height={60} />
          <YAxis domain={[0, 100]} />
          <Tooltip formatter={(val) => `${val ?? 0}%`} />
          <Bar dataKey="interest" fill="#2563eb" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function Charts({ data }: ChartsProps) {
  const calls = data?.calls || [];
  if (calls.length === 0) return null;

  return (
    <div className="charts-section">
      <div className="charts-grid">
        <CallStatusChart calls={calls} />
        <InterestChart calls={calls} />
        <QualificationFunnel calls={calls} />
        <DurationDistribution calls={calls} />
        <LoanStatusChart calls={calls} />
      </div>
      <InterestPercentageChart calls={calls} />
    </div>
  );
}
